import { datadogRum } from "@datadog/browser-rum";
import structuredClone from "@ungap/structured-clone";

if (!("structuredClone" in globalThis)) {
  globalThis.structuredClone = structuredClone as any;
}

/**
 * Pin `Reflect.construct` to a native implementation (SN-19611).
 *
 * Box Content Preview — loaded lazily from Box's CDN by Box Elements, at a
 * version we do not control — bundles an old core-js ES5 `Reflect` polyfill and
 * assigns it over the global `Reflect.construct`. Its slow path (taken when
 * `newTarget !== target`) constructs via `Function.apply.call(target, obj, args)`,
 * i.e. without `new`.
 *
 * Alone that is invisible. But extensions that proxy constructors (NordPass, in
 * SN-19611) route every `new X()` through `Reflect.construct` and pass the proxy
 * as `newTarget` — which lands on that slow path, so native DOM constructors are
 * called as plain functions and throw. MUI's Tabs does `new MutationObserver(...)`
 * in a layout effect, so every tabbed route (all of /patients/*, /inbox) died at
 * `RouteErrorBoundary`.
 *
 * Pinning the Box version is not an escape: 2.93.0 ships the same polyfill.
 */
const isNativeFunction = (fn: unknown) =>
  typeof fn === "function" &&
  Function.prototype.toString.call(fn).includes("[native code]");

const pristineReflectConstruct = ((): typeof Reflect.construct => {
  // Common case — nothing has touched it yet, so our own realm's copy is good.
  if (isNativeFunction(Reflect.construct)) return Reflect.construct;

  // An extension patched it before we booted. Recover a clean copy from a fresh
  // realm; pinning the extension's wrapper risks recursing through our getter.
  // The frame stays attached — a detached realm's functions are not safe to call.
  try {
    const frame = document.createElement("iframe");
    frame.setAttribute("aria-hidden", "true");
    frame.style.display = "none";
    document.documentElement.appendChild(frame);
    const recovered = (frame.contentWindow as any)?.Reflect?.construct;
    if (isNativeFunction(recovered))
      return recovered as typeof Reflect.construct;
    frame.remove();
  } catch {
    // Fall through — a wrapped `Reflect.construct` still beats no guard.
  }
  return Reflect.construct;
})();

// An action, not an error: the guard succeeded. Box's assignment is
// unconditional, so any session that opens E-Documents produces one of these —
// at `sessionSampleRate: 100` an error here would be a recurring fixture of the
// RUM error stream and could mask real ones. Latched to once per session, which
// is enough to tell us when Box ships a fixed bundle and this guard can go.
// The latch is per-session, not per-script: Box gets there first, so a second
// script overwriting us later in the same session goes unreported.
let overwriteReported = false;

function handleReflectConstructOverwrite(replacement: unknown): void {
  // Latched before the telemetry call, not inside the try: if anything on
  // datadogRum's own path assigns Reflect.construct, the setter re-enters here
  // and has to bail rather than recurse.
  if (overwriteReported) return;
  overwriteReported = true;
  try {
    datadogRum.addAction("reflect_construct_overwrite_blocked", {
      // The caller's frame names the offending script and its version — the
      // field that actually tells us when Box ships a fix. `addError` carried it
      // for free on the Error's stack; on an action we capture it ourselves.
      stack: new Error().stack?.slice(0, 500),
      replacement:
        typeof replacement === "function"
          ? Function.prototype.toString.call(replacement).slice(0, 200)
          : String(replacement)
    });
  } catch {
    // Telemetry must never break the guard. This setter runs synchronously
    // inside the assigning script's statement, so a throw would abort its
    // module evaluation — taking Box Preview down instead of saving it.
  }
}

Object.defineProperty(Reflect, "construct", {
  configurable: true,
  enumerable: false,
  get: () => pristineReflectConstruct,
  set: handleReflectConstructOverwrite
});
